/**
 * API client for the Lanz Dispatch backend.
 *
 * All calls go through Vercel (production) or Vite dev proxy (local),
 * prefixed with /api — so we never hardcode the backend URL in the frontend.
 *
 * Authentication: every request includes the Clerk session token in the
 * Authorization header. The token is fetched fresh each call because
 * Clerk rotates it.
 *
 * Usage:
 *   const api = useApi();
 *   const { drivers } = await api.get('/drivers');
 *   const newDriver = await api.post('/drivers', { first_name: 'X', ... });
 *
 *   // Binary downloads (PDFs, CSVs, images) — returns a { blob, filename, response }:
 *   const { blob, filename } = await api.getBlob('/vehicles/abc-123/compliance-pdf');
 */
import { useAuth } from '@clerk/clerk-react';
import { useCallback } from 'react';
const API_BASE = '/api';
/**
 * Error thrown by the API client. Has statusCode, fieldErrors (for 400s),
 * and a human-readable message.
 */
export class ApiError extends Error {
    constructor({ statusCode, message, fieldErrors, response }) {
        super(message);
        this.name = 'ApiError';
        this.statusCode = statusCode;
        this.fieldErrors = fieldErrors || {};
        this.response = response;
    }
}
async function handleResponse(res) {
    const contentType = res.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');
    const body = isJson ? await res.json().catch(() => ({})) : await res.text();
    if (!res.ok) {
        throw new ApiError({
            statusCode: res.status,
            message: body?.error || body?.message || `Request failed (${res.status})`,
            fieldErrors: body?.fieldErrors,
            response: body,
        });
    }
    return body;
}

/**
 * Parse the filename out of a Content-Disposition header. Handles both
 * `attachment; filename="x.pdf"` and `attachment; filename=x.pdf` shapes.
 * Returns null if no filename can be extracted.
 */
function parseFilenameFromDisposition(disposition) {
    if (!disposition) return null;
    const match = disposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)"?/i);
    return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Hook providing authenticated API methods.
 * Must be used inside a component (uses React hooks).
 */
export function useApi() {
    const { getToken } = useAuth();
    const request = useCallback(async (method, path, { body, query, headers = {} } = {}) => {
        const token = await getToken();
        const url = new URL(`${window.location.origin}${API_BASE}${path}`);
        if (query) {
            for (const [k, v] of Object.entries(query)) {
                if (v !== undefined && v !== null && v !== '') {
                    url.searchParams.set(k, v);
                }
            }
        }
        const res = await fetch(url.toString(), {
            method,
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
                ...headers,
            },
            body: body !== undefined ? JSON.stringify(body) : undefined,
        });
        return handleResponse(res);
    }, [getToken]);

    /**
     * Download a binary response (PDF, CSV, image, etc.) as a Blob.
     * Bypasses handleResponse, which assumes JSON/text and would corrupt binary data.
     *
     * Returns: { blob, filename, response }
     *   - blob: the response body as a Blob
     *   - filename: parsed from Content-Disposition, or null if not present
     *   - response: the raw Response in case headers/status are needed
     *
     * Errors are still wrapped in ApiError using whatever JSON body the server
     * returned (so 400/404/500 with JSON error bodies surface cleanly).
     */
    const getBlob = useCallback(async (path, { query, headers = {} } = {}) => {
        const token = await getToken();
        const url = new URL(`${window.location.origin}${API_BASE}${path}`);
        if (query) {
            for (const [k, v] of Object.entries(query)) {
                if (v !== undefined && v !== null && v !== '') {
                    url.searchParams.set(k, v);
                }
            }
        }
        const res = await fetch(url.toString(), {
            method: 'GET',
            headers: {
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
                ...headers,
            },
        });
        if (!res.ok) {
            // Try to read a JSON error body — fall through to a generic message if not JSON.
            let detail = '';
            let parsedBody = null;
            try {
                parsedBody = await res.json();
                detail = parsedBody?.error || parsedBody?.message || '';
            } catch (_) {
                // ignore — non-JSON error response
            }
            throw new ApiError({
                statusCode: res.status,
                message: detail || `Download failed (${res.status})`,
                fieldErrors: parsedBody?.fieldErrors,
                response: parsedBody,
            });
        }
        const blob = await res.blob();
        const filename = parseFilenameFromDisposition(res.headers.get('content-disposition'));
        return { blob, filename, response: res };
    }, [getToken]);

    return {
        get: (path, opts) => request('GET', path, opts),
        post: (path, body, opts) => request('POST', path, { ...opts, body }),
        patch: (path, body, opts) => request('PATCH', path, { ...opts, body }),
        put: (path, body, opts) => request('PUT', path, { ...opts, body }),
        delete: (path, opts) => request('DELETE', path, opts),
        getBlob,
    };
}

/**
 * Trigger a browser download for a Blob. Handles the boilerplate of
 * creating an object URL, anchor element, click, and cleanup.
 * Use after api.getBlob() when you want to save the result to disk.
 *
 * Example:
 *   const { blob, filename } = await api.getBlob('/some/file.pdf');
 *   downloadBlob(blob, filename || 'fallback.pdf');
 */
export function downloadBlob(blob, filename) {
    const objectUrl = window.URL.createObjectURL(blob);
    const link = window.document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
    window.URL.revokeObjectURL(objectUrl);
}
